import React, { useState, useRef, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { FaBars, FaTimes, FaHome, FaSearch, FaTimes as FaClose, FaGlobe } from "react-icons/fa";
import RPB_Logo from '../../images/RPB_Logo.png';
import '../../CSS/NavbarMobile.css';
import botom from '../../images/botom.png';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../hooks/LanguageContext';
import { useLanguageData } from '../../hooks/useLanguageData';
import SearchModal from './SearchModal'; // ✅ Import du composant SearchModal

function NavbarMobile() {
    const [isOpen, setIsOpen] = useState(false);
    const [showSearchModal, setShowSearchModal] = useState(false);
    const [showAppointmentModal, setShowAppointmentModal] = useState(false);
    const [searchKeyword, setSearchKeyword] = useState(''); // ✅ Renommé pour cohérence
    const [appointmentData, setAppointmentData] = useState({
        nom: '', prenom: '', email: '', message: ''
    });
    const [menuItems, setMenuItems] = useState([]);
    const searchInputRef = useRef(null);
    const { t, i18n } = useTranslation();
    const { currentLanguage, changeLanguage } = useLanguage();

    // ✅ Récupérer les résultats de recherche
    const { 
        data: searchData, 
        loading: searchLoading, 
        error: searchError 
    } = useLanguageData(searchKeyword ? `/api/v1/embassy/search?keywords=${encodeURIComponent(searchKeyword)}` : null);

    // Récupérer les menus depuis l'API
    const { 
        data: menuResponse, 
        loading: menuLoading 
    } = useLanguageData('/api/v1/embassy/useful-links-category?slugCategory=links-nav-usa');

    // Fonction pour extraire le numéro du slug
    const extractNumberFromSlug = (slug) => {
        const match = slug.match(/(\d+)/);
        return match ? parseInt(match[1], 10) : 0;
    };

    // ✅ Fonction de recherche
    const performSearch = async (query) => {
        setSearchKeyword(query);
    };

    // Construire la structure des menus
    useEffect(() => {
        if (menuResponse?.data && Array.isArray(menuResponse.data)) {
            console.log('📊 Données API menus mobile reçues:', menuResponse);
            
            // Trier les données par le numéro dans le slug
            const sortedData = [...menuResponse.data].sort((a, b) => {
                const numA = extractNumberFromSlug(a.slug);
                const numB = extractNumberFromSlug(b.slug);
                return numA - numB;
            });
            
            // Organiser les données en structure hiérarchique
            const menuGroups = {};
            
            sortedData.forEach(item => {
                const slug = item.slug;
                
                if (slug.startsWith('menu')) {
                    // C'est un menu principal
                    if (!menuGroups[slug]) {
                        menuGroups[slug] = {
                            main: item,
                            subMenus: []
                        };
                    } else {
                        menuGroups[slug].main = item;
                    }
                } else if (slug.startsWith('submenu')) {
                    // C'est un sous-menu - déterminer à quel menu il appartient
                    const match = slug.match(/submenu(\d+)_menu(\d+)/);
                    if (match) {
                        const menuNumber = match[2];
                        const menuKey = `menu${menuNumber}`;
                        
                        if (!menuGroups[menuKey]) {
                            menuGroups[menuKey] = {
                                main: null,
                                subMenus: []
                            };
                        }
                        menuGroups[menuKey].subMenus.push(item);
                    }
                }
            });
            
            // Trier les sous-menus par le numéro dans le slug également
            Object.keys(menuGroups).forEach(key => {
                if (menuGroups[key].subMenus.length > 0) {
                    menuGroups[key].subMenus.sort((a, b) => {
                        const numA = extractNumberFromSlug(a.slug);
                        const numB = extractNumberFromSlug(b.slug);
                        return numA - numB;
                    });
                }
            });
            
            // Convertir en tableau et construire les items de menu
            const menuItemsArray = [
                // Item Accueil
                { 
                    label: <FaHome size={30} />, 
                    path: "/" 
                }
            ];
            
            // Ajouter les menus dans l'ordre
            const menuOrder = ['menu1', 'menu2', 'menu3', 'menu4', 'menu5', 'menu6', 'menu7', 'menu8'];
            
            menuOrder.forEach(menuKey => {
                const group = menuGroups[menuKey];
                if (group && group.main) {
                    const menuItem = {
                        label: group.main.name,
                        path: "#",
                        isTitle: true
                    };
                    
                    // Ajouter les sous-menus s'il y en a (déjà triés par slug)
                    if (group.subMenus.length > 0) {
                        menuItem.subMenu = group.subMenus.map(sub => ({
                            label: sub.name,
                            path: sub.linkAccess || "#",
                            external: sub.linkAccess && sub.linkAccess.startsWith('http')
                        }));
                    } else {
                        // Si pas de sous-menu, créer un sous-menu avec l'élément principal
                        menuItem.subMenu = [{
                            label: group.main.name,
                            path: group.main.linkAccess || "#",
                            external: group.main.linkAccess && group.main.linkAccess.startsWith('http')
                        }];
                    }
                    
                    // Marquer le dernier menu (Contactez-nous) comme spécial
                    if (menuKey === 'menu8') {
                        menuItem.special = true;
                    }
                    
                    menuItemsArray.push(menuItem);
                }
            });
            
            setMenuItems(menuItemsArray);
        }
    }, [menuResponse]);

    const handleSearchClick = () => {
        setIsOpen(false);
        setShowSearchModal(true);
    };

    const handleAppointmentClick = () => {
        setIsOpen(false);
        setShowAppointmentModal(true);
    };

    const handleCloseModal = () => {
        setShowSearchModal(false);
        setShowAppointmentModal(false);
        setSearchKeyword('');
        setAppointmentData({ nom: '', prenom: '', email: '', message: '' });
    };

    const handleAppointmentSubmit = (e) => {
        e.preventDefault();
        alert(`Formulaire envoyé pour : ${appointmentData.nom} ${appointmentData.prenom}`);
        handleCloseModal();
    };

    const handleBackdropClick = (e) => { 
        if (e.target === e.currentTarget) handleCloseModal(); 
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setAppointmentData(prev => ({ ...prev, [name]: value }));
    };

    const handleLanguageChange = (event) => {
        const newLanguage = event.target.value;
        changeLanguage(newLanguage);
    };

    // Si chargement, afficher un spinner simple
    if (menuLoading) {
        return (
            <div className="navbar-mobile">
                <div className="navbar-mobile-header">
                    <button className="menu-toggle" onClick={() => {}}>
                        <FaBars size={30} />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="navbar-mobile">
                <div className="navbar-mobile-header">
                    <button
                        className="menu-toggle"
                        onClick={() => setIsOpen(!isOpen)}
                    >
                        {isOpen ? <FaTimes size={30} /> : <FaBars size={30} />} 
                    </button>
                </div>

                {isOpen && (
                    <div 
                        className="navbar-mobile-overlay"
                        onClick={() => setIsOpen(false)}
                    />
                )}

                <div className={`navbar-mobile-menu ${isOpen ? 'open' : ''}`}>
                    <div className="navbar-mobile-content">
                        <ul>
                            {menuItems.map((item, index) => {
                                if (item.subMenu) {
                                    return (
                                        <React.Fragment key={index}>
                                            <li className="menu-title">
                                                <span className="title-text">{item.label}</span>
                                            </li>
                                            
                                            {item.subMenu.map((subItem, subIndex) => (
                                                <li key={`${index}-${subIndex}`} className="submenu-item">
                                                    {subItem.onClick ? (
                                                        <button
                                                            onClick={() => {
                                                                subItem.onClick();
                                                            }}
                                                            className={`submenu-link ${item.special ? "special-btn" : ""}`}
                                                        >
                                                            {subItem.label}
                                                        </button>
                                                    ) : subItem.external ? (
                                                        <a
                                                            href={subItem.path}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            onClick={() => setIsOpen(false)}
                                                            className={item.special ? "special-btn" : ""}
                                                        >
                                                            {subItem.label}
                                                        </a>
                                                    ) : (
                                                        <NavLink
                                                            to={subItem.path}
                                                            onClick={() => setIsOpen(false)}
                                                            className={item.special ? "special-btn" : ""}
                                                        >
                                                            {subItem.label}
                                                        </NavLink>
                                                    )}
                                                </li>
                                            ))}
                                        </React.Fragment>
                                    );
                                } else {
                                    return (
                                        <li key={index}>
                                            <NavLink
                                                to={item.path}
                                                onClick={() => setIsOpen(false)}
                                                target={item.external ? "_blank" : "_self"}
                                            >
                                                {item.label}
                                            </NavLink>
                                        </li>
                                    );
                                }
                            })}
                            
                            {/* SÉLECTEUR DE LANGUE AJOUTÉ ICI */}
                            <li className="language-selector-mobile">
                                <div className="language-selector-wrapper">
                                    <FaGlobe className="globe-icon" />
                                    <select
                                        className="mobile-lang-select"
                                        value={currentLanguage}
                                        onChange={handleLanguageChange}
                                        style={{
                                            backgroundImage: `url(${botom})`,
                                            backgroundRepeat: "no-repeat",
                                            backgroundPosition: "right 10px center",
                                            backgroundSize: "20px",
                                            paddingRight: "40px",
                                            WebkitAppearance: "none",
                                            MozAppearance: "none",
                                            appearance: "none"
                                        }}
                                    >
                                        <option value="fr">Français</option>
                                        <option value="en">English</option>
                                        <option value="zh">中文</option>
                                    </select>
                                </div>
                            </li>

                            <li className="search-item">
                                <button 
                                    className="search-button"
                                    onClick={handleSearchClick}
                                    aria-label="Recherche"
                                >
                                    <FaSearch size={30} />
                                    <span className="search-text">Recherche</span>
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* ✅ Modal de recherche avec SearchModal */}
            <SearchModal
                isOpen={showSearchModal}
                onClose={() => {
                    setShowSearchModal(false);
                    setSearchKeyword('');
                }}
                onSearch={performSearch}
                initialQuery=""
                isSearching={searchLoading}
                searchResults={searchData?.data}
                searchError={searchError?.message}
            />

            {/* Modal rendez-vous */}
            {showAppointmentModal && (
                <div className="appointment-modal-backdrop" onClick={handleBackdropClick}>
                    <div className="appointment-modal-content">
                        <div className="appointment-modal-header">
                            <h2 className="appointment-modal-title">{t('appointment.title')}</h2>
                            <button
                                className="appointment-modal-close"
                                onClick={handleCloseModal}
                                aria-label="Fermer"
                            >
                                <FaClose />
                            </button>
                        </div>
                        <div className="appointment-modal-body">
                            <form className="appointment-form" onSubmit={handleAppointmentSubmit}>
                                <div className="form-row">
                                    <div className="form-group">
                                        <input
                                            type="text"
                                            name="nom"
                                            placeholder={t('appointment.lastName')}
                                            className="form-input"
                                            value={appointmentData.nom}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <input
                                            type="text"
                                            name="prenom"
                                            placeholder={t('appointment.firstName')}
                                            className="form-input"
                                            value={appointmentData.prenom}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="form-group">
                                    <input
                                        type="email"
                                        name="email"
                                        placeholder={t('appointment.email')}
                                        className="form-input"
                                        value={appointmentData.email}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <textarea
                                        name="message"
                                        className="form-textarea"
                                        value={appointmentData.message}
                                        onChange={handleInputChange}
                                        placeholder={t('appointment.message')}
                                    />
                                </div>
                                <button
                                    type="submit"
                                    className="appointment-submit-btn"
                                    disabled={!appointmentData.nom.trim() || !appointmentData.prenom.trim() || !appointmentData.email.trim()}
                                >
                                    {t('appointment.submit')}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Styles CSS */}
            <style>{`
                /* Modal de rendez-vous */
                .appointment-modal-backdrop {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background-color: rgba(10, 43, 87, 0.85);
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    z-index: 2000;
                    padding: 20px;
                }
                
                .appointment-modal-content {
                    background: #e7ebf0;
                    width: 90%;
                    max-width: 650px;
                    max-height: 85vh;
                    border-radius: 12px;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.3);
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                }
                
                .appointment-modal-header {
                    padding: 1rem 1.5rem;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    border-bottom: 1px solid #d0d0d0;
                    background: #e7ebf0;
                    flex-shrink: 0;
                }
                
                .appointment-modal-title {
                    margin: 0;
                    font-size: 1.5rem;
                    font-weight: 700;
                    color: #0a2c57;
                }
                
                .appointment-modal-close {
                    background: none;
                    border: 1px solid #0a2c57;
                    color: #0a2c57;
                    font-size: 1.2rem;
                    cursor: pointer;
                    width: 40px;
                    height: 40px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 8px;
                    transition: all 0.3s ease;
                }
                
                .appointment-modal-close:hover {
                    background-color: rgba(10, 44, 87, 0.1);
                }
                
                .appointment-modal-body {
                    padding: 1.5rem;
                    overflow-y: auto;
                    flex: 1;
                }
                
                .appointment-form {
                    display: flex;
                    flex-direction: column;
                    gap: 1rem;
                }
                
                .form-row {
                    display: flex;
                    gap: 1rem;
                    flex-wrap: wrap;
                }
                
                .form-group {
                    display: flex;
                    flex-direction: column;
                    flex: 1;
                    min-width: 180px;
                }
                
                .form-input, .form-textarea {
                    padding: 0.75rem;
                    border: 1px solid #ddd;
                    border-radius: 8px;
                    font-size: 0.95rem;
                    font-family: 'Montserrat', sans-serif;
                    background-color: white;
                    transition: border-color 0.3s ease;
                }
                
                .form-input:focus, .form-textarea:focus {
                    outline: none;
                    border-color: #0a2c57;
                }
                
                .form-textarea {
                    min-height: 100px;
                    resize: vertical;
                }
                
                .appointment-submit-btn {
                    background-color: #8a1538 !important;
                    color: white;
                    border: none;
                    padding: 0.75rem 1.5rem;
                    border-radius: 8px;
                    font-size: 1rem;
                    font-weight: 600;
                    cursor: pointer;
                    width: 100%;
                    transition: background-color 0.3s ease;
                }
                
                .appointment-submit-btn:hover:not(:disabled) {
                    background-color: #6a0f2c !important;
                }
                
                .appointment-submit-btn:disabled {
                    background-color: #cccccc !important;
                    cursor: not-allowed;
                }

                /* Responsive */
                @media (max-width: 768px) {
                    .appointment-modal-content {
                        max-width: 95%;
                        max-height: 90vh;
                    }
                    
                    .appointment-modal-header {
                        padding: 0.8rem 1rem;
                    }
                    
                    .appointment-modal-title {
                        font-size: 1.2rem;
                    }
                    
                    .appointment-modal-body {
                        padding: 1rem;
                    }
                    
                    .form-row {
                        flex-direction: column;
                        gap: 0.8rem;
                    }
                    
                    .form-row .form-group {
                        min-width: 100%;
                    }
                }

                @media (max-width: 480px) {
                    .appointment-modal-header {
                        padding: 0.6rem 0.8rem;
                    }
                    
                    .appointment-modal-title {
                        font-size: 1rem;
                    }
                    
                    .appointment-modal-close {
                        width: 30px;
                        height: 30px;
                        font-size: 0.9rem;
                    }
                    
                    .appointment-modal-body {
                        padding: 0.8rem;
                    }
                    
                    .form-input, .form-textarea {
                        padding: 0.5rem;
                        font-size: 0.85rem;
                    }
                }
            `}</style>
        </>
    );
}

export default NavbarMobile;